"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

interface MenuItem {
  id: string;
  iconSrc: string;
  text: string;
  link?: string;
  func?: () => void;
  disabled?: boolean;
}

interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: MenuItem[];
  className?: string;
  hasIcon?: boolean;
}

export default function DropdownMenu({
  trigger,
  items,
  className = "",
  hasIcon = false,
}: Readonly<DropdownMenuProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 cursor-pointer"
        aria-expanded={isOpen}
      >
        {trigger}
        {hasIcon && (
          <ChevronDown
            size={20}
            className={`transition-transform duration-300 motion-reduce:transition-none text-def ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      <div
        className={`
          absolute left-0 mt-2 w-60 z-50
          grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none
          ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr] pointer-events-none"}
        `}
      >
        <div className="overflow-hidden min-h-0">
          <div
            className="
              origin-top-left rounded-lg
              bg-white p-4
              shadow-xl ring-1 ring-black/5
              focus:outline-none
            "
          >
            <div className="py-1 divide-y divide-gray-100">
              {items.map((item) => {
                const content = (
                  <>
                    <div className="shrink-0">
                      <Image
                        src={item.iconSrc}
                        alt={item.text}
                        width={24}
                        height={24}
                        className="h-6 w-6 object-contain"
                      />
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </>
                );

                const sharedClasses = `
                  flex items-center gap-3 px-4 py-2
                  text-sm text-def
                  hover:bg-secondary
                  transition-colors
                  w-full text-left
                `;

                if (item.func) {
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        item.func?.();
                        setIsOpen(false);
                      }}
                      disabled={item.disabled}
                      className={`${sharedClasses} cursor-pointer`}
                    >
                      {content}
                    </button>
                  );
                }

                if (item.link) {
                  return (
                    <Link
                      key={item.id}
                      href={item.link}
                      className={sharedClasses}
                      onClick={() => setIsOpen(false)}
                    >
                      {content}
                    </Link>
                  );
                }

                return null;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
